import React from 'react';
import { Modal, Alert, Typography, List, Space, Button } from 'antd';
import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { ZipcodeConflictResult } from '../hooks/useZipcodeConflictCheck';

const { Text } = Typography;

interface ZipcodeConflictModalProps {
  visible: boolean;
  conflictResult: ZipcodeConflictResult;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const ZipcodeConflictModal: React.FC<ZipcodeConflictModalProps> = ({
  visible,
  conflictResult,
  onConfirm,
  onCancel,
  loading = false
}) => {
  const { hasAreaConflict, hasWarehouseConflict, conflictingAreas, conflictingWarehouses } = conflictResult;

  const showContinueButton = hasAreaConflict && !hasWarehouseConflict;

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span>Zipcode Conflict Warning</span>
        </Space>
      }
      open={visible}
      onOk={showContinueButton ? onConfirm : undefined}
      onCancel={onCancel}
      okText={showContinueButton ? "Continue Save" : undefined}
      cancelText="Cancel"
      confirmLoading={loading}
      width={600}
      okButtonProps={showContinueButton ? { danger: true } : undefined}
      footer={
        showContinueButton ? undefined : [
          <Button 
            key="cancel" 
            onClick={onCancel}
          >
            Cancel
          </Button>
        ]
      }
    >
      <div className="space-y-4">
        {hasAreaConflict && (
          <Alert
            type="warning"
            message="Areas Will Be Affected"
            description={
              <div>
                <Text>The following delivery areas contain zipcodes that you are removing from the delivery zone. These zipcodes will also be removed from the areas:</Text>
                <List
                  className="mt-2"
                  size="small"
                  bordered
                  dataSource={conflictingAreas}
                  renderItem={(area) => (
                    <List.Item>
                      <div>
                        <Text strong>Area {area.id}</Text>
                        {area.sortingCode && (
                          <Text className="ml-2" type="secondary">
                            (Sorting Code: {area.sortingCode})
                          </Text>
                        )}
                        <br />
                        <Text type="secondary">
                          Affected zipcodes: {area.conflictingZipcodes.join(', ')}
                        </Text>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            }
            icon={<WarningOutlined />}
          />
        )}

        {hasWarehouseConflict && (
          <Alert
            type="error"
            message="Warehouse Conflict Detected"
            description={
              <div>
                <Text>The following zipcodes are already assigned to other warehouses:</Text>
                <List
                  className="mt-2"
                  size="small"
                  bordered
                  dataSource={conflictingWarehouses}
                  renderItem={(conflict) => (
                    <List.Item>
                      <div>
                        <Text strong>{conflict.warehouseName}</Text>
                        <Text className="ml-2" type="secondary">
                          (ID: {conflict.warehouseId})
                        </Text>
                        <br />
                        <Text type="secondary">
                          Conflicting zipcodes: {conflict.zipcodes.join(', ')}
                        </Text>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            }
            icon={<ExclamationCircleOutlined />}
          />
        )}

        <Alert
          type="info"
          message={
            hasWarehouseConflict 
              ? "Cannot save: Please remove the conflicting zipcodes first." 
              : "Please confirm if you want to continue with these changes."
          }
          showIcon
        />
      </div>
    </Modal>
  );
};

export default ZipcodeConflictModal; 